import { t } from '@trimble/i18n';
import {
  FileFolderItem,
  MissionItem,
  PointCloudItem,
  TrajectoryItem,
} from '@mobile-mapping/providers';
import { TreeNodeDefinition } from '@trimble/ui-components/solidjs';
import { UnreachableCaseError } from 'ts-essentials';
import trajectoryIcon from '../../assets/icons/trajectory.svg';
import pointCloudIcon from '../../assets/icons/point-cloud.svg';

export const TRAJECTORY_ROOT_NODE_ID = 'trajectories';
export const POINT_CLOUD_ROOT_NODE_ID = 'pointClouds';
export const MODEL_ROOT_NODE_ID = 'tcModels';
export const MISSION_ROOT_NODE_ID = 'missions';

export type ProjectDataTreeCategory =
  | 'missions'
  | 'trajectories'
  | 'models'
  | 'pointClouds';

export const PROJECT_DATA_TREE_CATEGORIES: readonly ProjectDataTreeCategory[] =
  ['missions', 'trajectories', 'models', 'pointClouds'];

export function getProjectDataCategoryFolderProps(
  category: ProjectDataTreeCategory
): Pick<TreeNodeDefinition<unknown>, 'label' | 'labelIcon' | 'labelSvgIcon'> {
  switch (category) {
    case 'missions':
      return {
        label: t('mobileMapping.missions.missions'),
        labelSvgIcon: trajectoryIcon,
      };
    case 'trajectories':
      return {
        label: t('mobileMapping.projectData.trajectories'),
        labelSvgIcon: trajectoryIcon,
      };
    case 'models':
      return {
        label: t('mobileMapping.projectData.models'),
        labelIcon: 'cube',
      };
    case 'pointClouds':
      return {
        label: t('mobileMapping.projectData.pointClouds'),
        labelSvgIcon: pointCloudIcon,
      };
    default:
      throw new UnreachableCaseError(category);
  }
}

export function getProjectDataCategoryToggleTooltip(
  category: ProjectDataTreeCategory
): string {
  switch (category) {
    case 'missions':
      return t('mobileMapping.missions.toggleRunVisibility');
    case 'trajectories':
      return t('mobileMapping.projectData.toggleTrajectoryVisibility');
    case 'models':
      return t('mobileMapping.projectData.toggleModelVisibility');
    case 'pointClouds':
      return t('mobileMapping.projectData.togglePointCloudVisibility');
    default:
      throw new UnreachableCaseError(category);
  }
}

const PATH_SORT_PRIORITY = -1;

export enum MissionTreeNodeType {
  Mission = 'mission',
  Path = 'path',
  Runs = 'runs',
  Run = 'run',
}

export interface MissionTreeNodeData {
  type: MissionTreeNodeType;
  mission?: MissionItem;
  run?: MissionItem['runs'][number];
}

export const buildTrajectoryNodes = (
  items: TrajectoryItem[]
): TreeNodeDefinition<TrajectoryItem>[] => {
  const itemNodes = items
    .filter((item) => item.mission === undefined)
    .map(
      (item) =>
        ({
          id: item.id,
          label: item.name,
          data: item,
          parentId: TRAJECTORY_ROOT_NODE_ID,
          isLeaf: true,
          toggleTooltip: t(
            'mobileMapping.projectData.toggleTrajectoryVisibility'
          ),
        }) satisfies TreeNodeDefinition<TrajectoryItem>
    );

  if (itemNodes.length === 0) return [];

  return [
    {
      id: TRAJECTORY_ROOT_NODE_ID,
      isLeaf: false,
      ...getProjectDataCategoryFolderProps('trajectories'),
    },
    ...itemNodes,
  ];
};

export const buildPointCloudNodes = (
  items: PointCloudItem[]
): TreeNodeDefinition<PointCloudItem>[] => {
  const itemNodes = items
    .filter((item) => item.mission === undefined)
    .map(
      (item) =>
        ({
          id: item.id,
          label: item.name,
          data: item,
          parentId: POINT_CLOUD_ROOT_NODE_ID,
          isLeaf: true,
          toggleTooltip: t(
            'mobileMapping.projectData.togglePointCloudVisibility'
          ),
        }) satisfies TreeNodeDefinition<PointCloudItem>
    );

  if (itemNodes.length === 0) return [];

  return [
    {
      id: POINT_CLOUD_ROOT_NODE_ID,
      isLeaf: false,
      ...getProjectDataCategoryFolderProps('pointClouds'),
    },
    ...itemNodes,
  ];
};

export const buildModelNodes = (
  items: FileFolderItem[]
): TreeNodeDefinition<FileFolderItem>[] => {
  const itemNodes = items.map((item) => {
    const isFolder = item.type === 'FOLDER';
    return {
      id: item.id,
      label: item.name,
      data: item,
      parentId: item.rootElement ? MODEL_ROOT_NODE_ID : item.parentId,
      isLeaf: !isFolder,
      toggleTooltip: isFolder
        ? undefined
        : t('mobileMapping.projectData.toggleModelVisibility'),
    } satisfies TreeNodeDefinition<FileFolderItem>;
  });

  if (itemNodes.length === 0) return [];

  return [
    {
      id: MODEL_ROOT_NODE_ID,
      isLeaf: false,
      ...getProjectDataCategoryFolderProps('models'),
    },
    ...itemNodes,
  ];
};

export const buildMissionNodes = (
  items: MissionItem[]
): TreeNodeDefinition<MissionTreeNodeData>[] => {
  const nodes: TreeNodeDefinition<MissionTreeNodeData>[] = [
    {
      id: MISSION_ROOT_NODE_ID,
      isLeaf: false,
      ...getProjectDataCategoryFolderProps('missions'),
    },
  ];

  for (const mission of items) {
    nodes.push({
      id: mission.id,
      label: mission.name,
      data: { type: MissionTreeNodeType.Mission, mission },
      parentId: MISSION_ROOT_NODE_ID,
      isLeaf: false,
      dataTestId: `${MissionTreeNodeType.Mission}-tree-item`,
    });

    const runsNodeId = `runs:${mission.id}`;
    nodes.push({
      id: runsNodeId,
      label: t('mobileMapping.runs.runs') + ` (${mission.runs.length})`,
      data: { type: MissionTreeNodeType.Runs, mission },
      parentId: mission.id,
      isLeaf: false,
      dataTestId: `${MissionTreeNodeType.Runs}-tree-item`,
    });

    for (const run of mission.runs) {
      nodes.push({
        id: run.id,
        label: run.name,
        data: { type: MissionTreeNodeType.Run, mission, run },
        parentId: runsNodeId,
        isLeaf: true,
        toggleTooltip: t('mobileMapping.missions.toggleRunVisibility'),
        dataTestId: `${MissionTreeNodeType.Run}-tree-item`,
      });
    }

    if (mission.pathId == null) continue;

    nodes.push({
      id: mission.pathId,
      label: t('mobileMapping.missions.path'),
      data: { type: MissionTreeNodeType.Path, mission },
      parentId: mission.id,
      isLeaf: true,
      sortPriority: PATH_SORT_PRIORITY,
      toggleTooltip: t('mobileMapping.missions.toggleMissionVisibility'),
      dataTestId: `${MissionTreeNodeType.Path}-tree-item`,
    });
  }

  return nodes.length === 1 ? [] : nodes;
};

export function getProjectDataCategoryRootId(
  category: ProjectDataTreeCategory
): string {
  switch (category) {
    case 'missions':
      return MISSION_ROOT_NODE_ID;
    case 'trajectories':
      return TRAJECTORY_ROOT_NODE_ID;
    case 'models':
      return MODEL_ROOT_NODE_ID;
    case 'pointClouds':
      return POINT_CLOUD_ROOT_NODE_ID;
    default:
      throw new UnreachableCaseError(category);
  }
}

export interface ProjectDataCategoryItems {
  readonly missions: readonly MissionItem[];
  readonly trajectories: readonly TrajectoryItem[];
  readonly models: readonly FileFolderItem[];
  readonly pointClouds: readonly PointCloudItem[];
}

export function buildProjectDataCategoryNodes(
  category: ProjectDataTreeCategory,
  items: ProjectDataCategoryItems
): TreeNodeDefinition<unknown>[] {
  switch (category) {
    case 'missions':
      return buildMissionNodes([...items.missions]);
    case 'trajectories':
      return buildTrajectoryNodes([...items.trajectories]);
    case 'models':
      return buildModelNodes([...items.models]);
    case 'pointClouds':
      return buildPointCloudNodes([...items.pointClouds]);
    default:
      throw new UnreachableCaseError(category);
  }
}
